import { ChangeDetectorRef, Component, OnInit } from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { MatButtonModule } from "@angular/material/button";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatInputModule } from "@angular/material/input";
import { MatSelectModule } from "@angular/material/select";
import { finalize } from "rxjs/operators";
import { Dispositivo } from "src/app/shared/model/asistencia/asistencia.model";
import { Msg } from "src/app/utilerias/mensajes/Msg";
import { AsistenciaService } from "../asistencia.service";
import { soloFecha } from "../formato-fecha";

/**
 * Los teléfonos con los que se registra asistencia, y su aprobación.
 *
 * POR QUE ESTA PANTALLA EXISTE
 * En el canal remoto la contraseña no basta: si bastara, prestarla sería
 * suficiente para que otro registrara por ti. El equipo vinculado ata el
 * registro a un teléfono concreto, y la aprobación es lo que hace que
 * «me cambié de teléfono» y «alguien más se dio de alta con mi cuenta»
 * dejen de parecerse.
 *
 * Pero esa exigencia solo sirve si alguien VE la solicitud. Sin esta
 * bandeja, el equipo nuevo se queda pendiente hasta que la persona
 * reclama que no puede registrar —que es justo el aviso que la
 * aprobación existe para adelantar—. Por eso abre en PENDIENTE.
 *
 * La lista es de la empresa entera y no de un empleado: quien administra
 * no sabe de antemano quién se dio de alta con un aparato nuevo.
 */
@Component({
  selector: "app-telefonos",
  standalone: true,
  templateUrl: "./telefonos.component.html",
  styleUrls: ["./telefonos.component.css"],
  imports: [
    CommonModule,
    FormsModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
  ],
})
export class TelefonosComponent implements OnInit {
  dispositivos: Dispositivo[] = [];
  loading = false;

  /** Abre en lo que hay que atender, no en todo. */
  filtroEstatus = "PENDIENTE";
  filtroBusqueda = "";

  constructor(
    private asistenciaService: AsistenciaService,
    private msg: Msg,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    queueMicrotask(() => {
      this.consultar();
      this.cdr.detectChanges();
    });
  }

  consultar(): void {
    this.loading = true;
    this.cdr.detectChanges();

    this.asistenciaService
      .consultarBandejaDispositivos(this.filtroEstatus || null)
      .pipe(
        finalize(() => {
          this.loading = false;
          this.cdr.detectChanges();
        })
      )
      .subscribe({
        next: (respuesta) => {
          this.dispositivos = this.filtrarLocal(respuesta?.list ?? []);
          if (respuesta?.pMensaje && respuesta.pCodRetorno !== "00000") {
            this.msg.warningCall(respuesta.pMensaje, null, null);
          }
        },
        error: () => {
          this.dispositivos = [];
          this.msg.errorCall("No se pudieron consultar los teléfonos.", null, null);
        },
      });
  }

  limpiarFiltros(): void {
    this.filtroEstatus = "PENDIENTE";
    this.filtroBusqueda = "";
    this.consultar();
  }

  aprobar(dispositivo: Dispositivo): void {
    this.msg.questionCall(
      `¿Autorizar el teléfono de ${dispositivo.nombreEmpleado}? Podrá registrar asistencia desde ese equipo.`,
      null,
      (respuesta: any) => {
        if (!respuesta?.value) {
          return;
        }
        this.asistenciaService.aprobarDispositivo(dispositivo).subscribe({
          next: (res) => this.trasCambiar(res, "El teléfono quedó autorizado."),
          error: () => this.msg.errorCall("No se pudo autorizar el teléfono.", null, null),
        });
      }
    );
  }

  revocar(dispositivo: Dispositivo): void {
    this.msg.questionCall(
      `¿Dar de baja el teléfono de ${dispositivo.nombreEmpleado}? Dejará de poder registrar desde ese equipo en el siguiente intento.`,
      null,
      (respuesta: any) => {
        if (!respuesta?.value) {
          return;
        }
        this.asistenciaService.revocarDispositivo(dispositivo).subscribe({
          next: (res) => this.trasCambiar(res, "El teléfono quedó dado de baja."),
          error: () => this.msg.errorCall("No se pudo dar de baja el teléfono.", null, null),
        });
      }
    );
  }

  etiquetaEstatus(estatus: string): string {
    const etiquetas: Record<string, string> = {
      PENDIENTE: "Espera autorización",
      ACTIVO: "Autorizado",
      REVOCADO: "Dado de baja",
    };
    return etiquetas[estatus] ?? estatus ?? "—";
  }

  /**
   * La forma también dice el estado, no solo el color: quien no distingue
   * rojo de verde tiene que poder leer la tabla igual.
   */
  claseEstatus(estatus: string): string {
    // Modificadores globales de table-page__status-badge (styles.css):
    // is-active / is-pending / is-inactive.
    if (estatus === "ACTIVO") {
      return "table-page__status-badge is-active";
    }
    if (estatus === "PENDIENTE") {
      return "table-page__status-badge is-pending";
    }
    return "table-page__status-badge is-inactive";
  }

  etiquetaEquipo(dispositivo: Dispositivo): string {
    const modelo = dispositivo.modelo || "Equipo sin nombre";
    return dispositivo.plataforma ? `${modelo} · ${dispositivo.plataforma}` : modelo;
  }

  /** El backend manda fecha y hora; en la bandeja basta el día. */
  soloFecha(valor: string | Date): string {
    return soloFecha(valor) || "—";
  }

  esPendiente(dispositivo: Dispositivo): boolean {
    return dispositivo.estatus === "PENDIENTE";
  }

  esRevocado(dispositivo: Dispositivo): boolean {
    return dispositivo.estatus === "REVOCADO";
  }

  // ------------------------------------------------------------------

  /**
   * El nombre se filtra en el navegador: la bandeja de una empresa es
   * corta —los pendientes son unos cuantos— y una ida al servidor por
   * cada tecla no compra nada.
   */
  private filtrarLocal(lista: Dispositivo[]): Dispositivo[] {
    const texto = this.filtroBusqueda?.trim().toLowerCase();
    if (!texto) {
      return lista;
    }
    return lista.filter((equipo) => {
      const donde = `${equipo.nombreEmpleado ?? ""} ${equipo.usuario ?? ""} ${equipo.modelo ?? ""}`;
      return donde.toLowerCase().includes(texto);
    });
  }

  private trasCambiar(respuesta: any, exito: string): void {
    if (respuesta?.pCodRetorno && respuesta.pCodRetorno !== "00000") {
      this.msg.warningCall(respuesta.pMensaje, null, null);
      return;
    }
    this.msg.successCall(exito, null, null);
    this.consultar();
  }
}
